import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Dumbbell } from "lucide-react";

function QRImage({ token }) {
  return (
    <div className="bg-white p-4">
      <QRCodeSVG value={token} size={220} bgColor="#ffffff" fgColor="#0B1120" level="M" includeMargin={false} />
    </div>
  );
}

export default function GymMembership() {
  const [member, setMember] = useState(null);
  const [plans] = useState([
    { key: "monthly", label: "Monthly", price: 1500, subtitle: "Flexible month-to-month" },
    { key: "quarterly", label: "Quarterly", price: 3999, subtitle: "3 months · save ₹501" },
    { key: "annual", label: "Annual", price: 12999, subtitle: "12 months · save ₹5001" },
  ]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/gym/my-membership").then((r) => { setMember(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const subscribe = async (plan) => {
    try {
      const { data } = await api.post("/gym/subscribe", { plan });
      toast.success(`Gym membership activated · ${formatINR(data.amount)} paid (mock)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="text-white/40">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-16" data-testid="gym-membership-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Fitness Gym · QR Check-In</div>
      <h1 className="font-display text-5xl md:text-6xl font-black uppercase leading-none tracking-tighter">
        Gym <span className="brand-blue">membership</span>.
      </h1>

      {member ? (
        <div className="mt-10 border border-white/12 bg-[#131A2B] p-8 grid md:grid-cols-2 gap-8" data-testid="active-membership">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest brand-blue mb-3">Active Membership</div>
            <div className="font-display text-3xl uppercase font-black">{member.plan.toUpperCase()} PLAN</div>
            <div className="text-sm text-white/60 mt-4">Member: {member.user_name}</div>
            <div className="text-sm text-white/60">Valid from: {member.start_date}</div>
            <div className="text-sm text-white/60">Valid until: <span className="brand-orange">{member.end_date}</span></div>
            <div className="text-sm text-white/60 mt-3">Total check-ins: <span className="brand-blue font-bold">{member.check_ins?.length || 0}</span></div>
            <div className="mt-6 text-xs font-mono text-white/40 leading-relaxed">
              Show this QR code at the gym entrance. Our scanner will verify and log your check-in instantly.
            </div>
          </div>
          <div className="flex flex-col items-center justify-center border border-white/12 bg-black p-6">
            <QRImage token={member.qr_token} />
            <div className="mt-4 font-mono text-xs text-white/40 break-all text-center">{member.qr_token}</div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-white/60 mt-6 max-w-2xl">Pick a plan below. On activation you'll get a unique QR code that our reception scanner uses for instant check-in.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.key} data-testid={`gym-plan-${p.key}`} className="border border-white/12 bg-[#131A2B] p-6 hover-lift">
                <Dumbbell size={20} className="brand-blue mb-4" />
                <div className="font-display text-2xl uppercase font-black">{p.label}</div>
                <div className="text-xs font-mono text-white/40 mt-1">{p.subtitle}</div>
                <div className="font-display text-4xl brand-blue mt-6">{formatINR(p.price)}</div>
                <button
                  onClick={() => subscribe(p.key)}
                  data-testid={`subscribe-${p.key}`}
                  className="mt-6 w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest text-xs hover:glow-blue"
                >
                  Activate {p.label}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
